import { avatarSeedForSlot } from "@productlane/ui"

export const MAIN_PORTAL_LABEL = "Main portal"

export interface PortalInstanceLike {
	id: string
	name: string
	slug?: string | null
	logoUrl?: string | null
	createdAt?: number | null
}

export interface PortalMark {
	id: string | null
	name: string
	slug: string | null
	logoUrl: string | null
	/** Picks the fallback colour; see `portalColorSeeds`. */
	colorSeed?: string | null
}

/**
 * One palette colour per portal, Main first and the rest in the order they
 * were created, so portals without a logo never share a colour and keep it
 * when another portal is renamed or added.
 */
export function portalColorSeeds(
	instances: readonly Pick<PortalInstanceLike, "id" | "createdAt">[],
): Map<string | null, string> {
	const seeds = new Map<string | null, string>([[null, avatarSeedForSlot(0)]])
	;[...instances]
		.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0))
		.forEach((instance, index) => {
			seeds.set(instance.id, avatarSeedForSlot(index + 1))
		})
	return seeds
}

/**
 * Name and logo only: every surface renders the mark through Avatar, the way
 * portal settings does, so the initials and the seeded colour come from there.
 */
export function portalMark(instance: PortalInstanceLike | null): PortalMark {
	if (!instance) {
		return { id: null, name: MAIN_PORTAL_LABEL, slug: null, logoUrl: null }
	}
	return {
		id: instance.id,
		name: instance.name,
		slug: instance.slug ?? null,
		logoUrl: instance.logoUrl ?? null,
	}
}

export function sortedPortalMarks(
	instances: readonly PortalInstanceLike[],
): PortalMark[] {
	if (instances.length === 0) return []
	const seeds = portalColorSeeds(instances)
	const named = [...instances]
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((instance) => ({
			...portalMark(instance),
			colorSeed: seeds.get(instance.id) ?? null,
		}))
	return [{ ...portalMark(null), colorSeed: seeds.get(null) ?? null }, ...named]
}

/** The main portal serves the bare domain; every other portal sits under its slug. */
export function portalPath(mark: Pick<PortalMark, "slug">): string {
	return mark.slug ? `/${mark.slug}` : "/"
}
